import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { ReceivableSummary } from "src/app/models/receivable-summary.model";
import { Receivable } from "src/app/models/receivable.model";
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";

@Component({
    selector: "portfolioaging-report",
    templateUrl: "./portfolioaging-report.component.html",
    styleUrls: ["./portfolioaging-report.component.scss"],
    standalone: false
})
export class PortfolioAgingReportComponent implements OnInit, OnDestroy {

    /** define if is loading */ 
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    /** A summary of aging data for the report. */
    protected summary: ReceivableSummary | null = null

    /** A list of receivables for the selected lot and/or corporation. */
    protected receivables: Receivable[] | null = null

    /** Pre-computed receivables groups to quickly respond to interactivity changes.  */
    protected grouped: Object = {}

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId () {
        return parseInt(this.form.get('lotId').value)
    }

    /**
     * Determine a human-readable string of 
     * the current sum filter user selection.
     */
    protected get summedBy (): string {
        switch((this.form.get('sum').value || '').toLowerCase()) {
            case 'principal':
                return 'Principal'
            case 'principalwithtax':
                return 'Principal + Tax'
            case 'payment':
                return 'Payment'
            case 'payoff':
                return 'Payoff'
        }

        return null
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            sum: new FormControl<string>('principal', []),
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] && 
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.summary = null
        this.receivables = []
        this.grouped = {}

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'portfolio aging', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        let id = parseInt(this.form.get('lotId').value)
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
        }

        this.loading = true
        await this.master.getAsync(`reports/portfolioaging${lotParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }
            
            this.summary = res.data.summary
            this.receivables = res.data.receivables
            this.constructGroups()
        })
    }

    /**
     * Group the receivables for later display based on user selections.
    */
    protected constructGroups = () => {
        if (!this.receivables || (this.receivables.length <= 0)) {
            return
        }

        for (const receivable of this.receivables) {
            const key: string = `${receivable.corporateId}-${receivable.dealershipId}-${receivable.lotId}`
            if (!this.grouped.hasOwnProperty(key)) {
                this.grouped[key] = {
                    name: receivable.lot,
                    summary: new ReceivableSummary(),
                    receivables: new Array<Receivable>(),
                    count: 0
                }
            }

            this.grouped[key].count++
            if (!this.grouped[key].summary.asOf || moment(receivable.day).isAfter(this.grouped[key].summary.asOf)) {
                this.grouped[key].summary.asOf = receivable.day
            }

            this.grouped[key].summary.balance += receivable.balance
            this.grouped[key].summary.taxBalance += receivable.taxBalance
            this.grouped[key].summary.payoff += receivable.payoff
            this.grouped[key].receivables.push(receivable)

            const daysPastDue = moment().diff(receivable.pastDueDate || receivable.due, 'days')
            receivable.daysPastDue = daysPastDue
            
            switch (true) {
                case (daysPastDue < 30):
                    this.grouped[key].summary.current++
                    this.grouped[key].summary.currentBalance += receivable.balance
                    this.grouped[key].summary.currentWithTaxBalance += receivable.balance + receivable.taxBalance
                    this.grouped[key].summary.currentPaymentBalance += receivable.paymentBalance
                    this.grouped[key].summary.currentPayoff += receivable.payoff
                    break
                case ((daysPastDue >= 30) && (daysPastDue <= 59)):
                    this.grouped[key].summary.thirtyDay++
                    this.grouped[key].summary.thirtyDayBalance += receivable.balance
                    this.grouped[key].summary.thirtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                    this.grouped[key].summary.thirtyDayPaymentBalance += receivable.paymentBalance
                    this.grouped[key].summary.thirtyDayPayoff += receivable.payoff
                    break
                case ((daysPastDue >= 60) && (daysPastDue <= 89)):
                    this.grouped[key].summary.sixtyDay++
                    this.grouped[key].summary.sixtyDayBalance += receivable.balance
                    this.grouped[key].summary.sixtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                    this.grouped[key].summary.sixtyDayPaymentBalance += receivable.paymentBalance
                    this.grouped[key].summary.sixtyDayPayoff += receivable.payoff
                    break
                case (daysPastDue >= 90):
                    this.grouped[key].summary.ninetyDay++
                    this.grouped[key].summary.ninetyDayBalance += receivable.balance
                    this.grouped[key].summary.ninetyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                    this.grouped[key].summary.ninetyDayPaymentBalance += receivable.paymentBalance
                    this.grouped[key].summary.ninetyDayPayoff += receivable.payoff
                    break
            }
        }
    }

    /**
     * Get the needed balance based on the sum user filter.
     * @param summary A receivable or receivable summary object that will contain the balance to retrieve.
     * @param bucket The aging bucket whose balance we need (current, thirtyDay, sixtyDay, ninetyDay)
     * @returns The balance based on the sum filter.
     */
    protected balance = (summary: Object, bucket: string): number => {
        switch((this.form.get('sum').value || '').toLowerCase()) {
            case 'principal':
                if (!bucket || (bucket.length <= 0)) {
                    return summary['balance']
                }

                return summary[`${bucket}Balance`]
            case 'principalwithtax':
                if (!bucket || (bucket.length <= 0)) {
                    return (summary['balance'] + summary['taxBalance'])
                }

                return summary[`${bucket}WithTaxBalance`]
            case 'payment':
                if (!bucket || (bucket.length <= 0)) {
                    return summary['paymentBalance']
                }

                return summary[`${bucket}PaymentBalance`]
            case 'payoff':
                if (!bucket || (bucket.length <= 0)) {
                    return summary['payoff']
                }

                return summary[`${bucket}Payoff`]
        }

        return 0
    }

    /**
     * Computed how many says past due the account is based on the given due date.
     * @param {Date} ago The current (or past) due date of the account.
     */
    protected daysAgo = (ago: Date): number => {
        return moment().diff(ago, 'days')
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
